import { useEffect, useId, useRef, type ReactNode } from 'react'

type ContextFlyoutProps = {
  open: boolean
  label: string
  children: ReactNode
  onClose: () => void
}

/**
 * Temporary contextual panel opened from the utility rail.
 * Overlays the workspace; does not permanently consume layout width.
 */
export function ContextFlyout({ open, label, children, onClose }: ContextFlyoutProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    focusable?.focus()
    return () => {
      previouslyFocused?.focus?.()
    }
  }, [open])

  if (!open || !children) return null

  return (
    <div className="context-flyout-root" data-testid="context-flyout">
      <button
        type="button"
        className="context-flyout__backdrop"
        aria-label="Close context panel"
        data-testid="context-flyout-backdrop"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="context-flyout"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="context-panel"
      >
        <div className="context-flyout__header">
          <h2 id={titleId} className="context-flyout__title">
            {label}
          </h2>
          <button type="button" className="btn ghost compact context-flyout__close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="context-flyout__body">{children}</div>
      </div>
    </div>
  )
}
